/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import BackButton from '../common/BackButton';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../../services/api';
import { BidResult } from '../../types';
import './BidResultForm.css';

const BidResultForm: React.FC = () => {
  const { bidId } = useParams<{ bidId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState<BidResult>({
    bidId: parseInt(bidId || '0'),
    bidAccepted: undefined,
    bidSubmittedDate: '',
    bidAcceptedDate: '',
    competingBidCount: undefined,
    knownCompetitors: '',
    notes: '',
  });

  useEffect(() => {
    if (bidId) {
      loadBidResult();
    }
  }, [bidId]);

  const loadBidResult = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getBidResultByBidId(parseInt(bidId || '0'));

      if (response.success) {
        if (response.bidResult) {
          // Format dates for date input (YYYY-MM-DD)
          const formatDateForInput = (dateString?: string) => {
            if (!dateString) return '';
            try {
              const date = new Date(dateString);
              return date.toISOString().slice(0, 10);
            } catch {
              return '';
            }
          };

          setFormData({
            ...response.bidResult,
            bidId: parseInt(bidId || '0'),
            bidSubmittedDate: formatDateForInput(response.bidResult.bidSubmittedDate),
            bidAcceptedDate: formatDateForInput(response.bidResult.bidAcceptedDate),
          });
        } else {
          // No existing result, start with empty form
          setFormData({
            bidId: parseInt(bidId || '0'),
            bidAccepted: undefined,
            bidSubmittedDate: '',
            bidAcceptedDate: '',
            competingBidCount: undefined,
            knownCompetitors: '',
            notes: '',
          });
        }
      } else {
        setError(response.error || 'Failed to load bid result');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while loading bid result');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: value === '' ? undefined : parseInt(value)
      }));
    } else if (name === 'bidAccepted') {
      setFormData(prev => ({
        ...prev,
        bidAccepted: value === '' ? undefined : value === 'true'
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setSaving(true);

    try {
      const payload: any = {
        bidId: formData.bidId,
      };

      if (formData.bidAccepted !== undefined) {
        payload.bidAccepted = formData.bidAccepted;
      }

      if (formData.bidSubmittedDate) {
        payload.bidSubmittedDate = formData.bidSubmittedDate;
      }

      if (formData.bidAcceptedDate) {
        payload.bidAcceptedDate = formData.bidAcceptedDate;
      }

      if (formData.competingBidCount !== undefined) {
        payload.competingBidCount = formData.competingBidCount;
      }

      if (formData.knownCompetitors) {
        payload.knownCompetitors = formData.knownCompetitors;
      }

      if (formData.notes) {
        payload.notes = formData.notes;
      }

      const response = await apiClient.saveBidResult(payload);

      if (response.success) {
        setSuccessMessage('Bid result saved successfully!');
        // Update form with saved data
        if (response.bidResult) {
          const formatDateForInput = (dateString?: string) => {
            if (!dateString) return '';
            try {
              const date = new Date(dateString);
              return date.toISOString().slice(0, 10);
            } catch {
              return '';
            }
          };

          setFormData({
            ...response.bidResult,
            bidId: parseInt(bidId || '0'),
            bidSubmittedDate: formatDateForInput(response.bidResult.bidSubmittedDate),
            bidAcceptedDate: formatDateForInput(response.bidResult.bidAcceptedDate),
          });
        }
      } else {
        setError(response.error || 'Failed to save bid result');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while saving bid result');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/bids');
  };

  if (loading) {
    return (
      <div className="bid-result-container">
        <BackButton to="/bids" />
        <div className="loading">Loading bid result...</div>
      </div>
    );
  }

  return (
    <div className="bid-result-container">
      <BackButton to="/bids" />
      <div className="bid-result-header">
        <h2>Bid Results - Bid #{bidId}</h2>
      </div>

      <form onSubmit={handleSubmit} className="bid-result-form">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="bidAccepted">Bid Status</label>
          <select
            id="bidAccepted"
            name="bidAccepted"
            value={formData.bidAccepted === undefined || formData.bidAccepted === null ? '' : formData.bidAccepted.toString()}
            onChange={handleInputChange}
            disabled={saving}
          >
            <option value="">Not Yet Determined</option>
            <option value="true">Accepted</option>
            <option value="false">Rejected</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="bidSubmittedDate">Bid Submitted Date</label>
          <input
            type="date"
            id="bidSubmittedDate"
            name="bidSubmittedDate"
            value={formData.bidSubmittedDate}
            onChange={handleInputChange}
            disabled={saving}
          />
        </div>

        <div className="form-group">
          <label htmlFor="bidAcceptedDate">Bid Accepted Date</label>
          <input
            type="date"
            id="bidAcceptedDate"
            name="bidAcceptedDate"
            value={formData.bidAcceptedDate}
            onChange={handleInputChange}
            disabled={saving}
          />
          <small style={{ color: '#666', fontSize: '0.85rem' }}>
            Only applicable if bid was accepted
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="competingBidCount">Number of Competing Bids</label>
          <input
            type="number"
            id="competingBidCount"
            name="competingBidCount"
            value={formData.competingBidCount || ''}
            onChange={handleInputChange}
            disabled={saving}
            min="0"
            placeholder="0"
          />
        </div>

        <div className="form-group">
          <label htmlFor="knownCompetitors">Known Competitors</label>
          <textarea
            id="knownCompetitors"
            name="knownCompetitors"
            value={formData.knownCompetitors}
            onChange={handleInputChange}
            disabled={saving}
            rows={4}
            placeholder="List known competitors (one per line or comma-separated)"
          />
        </div>

        <div className="form-group">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            disabled={saving}
            rows={6}
            placeholder="Additional notes about the bid result"
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="cancel-button"
          >
            Back to Bid
          </button>
          <button
            type="submit"
            disabled={saving}
            className="submit-button"
          >
            {saving ? 'Saving...' : 'Save Results'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BidResultForm;
